import { ArrowDown, ArrowDown2, ArrowUp2 } from "iconsax-reactjs"
import { RatIcon } from "lucide-react"
import { useEffect, useMemo, useState } from "react"

import { Carousel, CarouselApi, CarouselContent, CarouselItem, Image } from "@/components/ui"
import { EFolderAssets } from "@/modules/helpers/enum"
import { cn } from "@/modules/helpers/hooks/shared/utils"
import { UI_TEXT } from "@/modules/helpers/tap-constants"
import { formatCurrency, formatDiscount } from "@/modules/helpers/tap-utils"
import { getImageByNameFromS3 } from "@/modules/helpers/utils"
import type { CampaignInspiration, Detail, DraftGallery, ReviewItem } from "@/modules/types"

// Product Info Card
interface ProductPrice {
  realPrice?: string
  originalPrice?: string
  discount?: string
}

interface ProductReview {
  reviewItems?: ReviewItem[]
}

interface ProductInfoProps {
  title?: string
  productId?: string
  price?: ProductPrice
  productDetailReview?: ProductReview
}

const parsePrice = (priceString?: string): number => {
  if (!priceString) return 0
  const numericString = priceString.replace(/[^\d.]/g, "").replace(/\./g, "")
  return Number(numericString) || 0
}

const formatPriceRange = (priceString?: string): string => {
  if (!priceString) return formatCurrency(0)
  const parts = priceString.split("-").map((p) => p.trim())
  if (parts.length === 1) return formatCurrency(parsePrice(parts[0]))
  return `${formatCurrency(parsePrice(parts[0]))} - ${formatCurrency(parsePrice(parts[1]))}`
}

export const ProductInfo = ({ title, productId, price }: ProductInfoProps) => {
  return (
    <div className="flex flex-col gap-2">
      <h2 className="text-sm font-semibold">{title}</h2>

      <div className="flex text-xs justify-between">
        <p className="font-medium">
          {UI_TEXT.PRODUCT_ID} {productId}
        </p>
      </div>

      <div className="flex gap-1">
        <p className="font-bold text-base text-gradient-primary">
          {formatPriceRange(price?.realPrice)}
        </p>

        <div className="flex flex-row gap-1.5 items-center">
          {price?.originalPrice && (
            <span className="font-normal text-xss tiny:text-xs text-gray-500 line-through">
              {formatPriceRange(price?.originalPrice)}
            </span>
          )}

          {price?.discount && (
            <div className="flex items-center gap-0.5">
              <ArrowDown size={12} color="red" variant="Outline" />
              <span className="text-red-600 font-medium text-[10px]">
                {formatDiscount(price.discount)}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// Product Description
export const ProductDescription: React.FC<{ data?: string }> = ({ data }) => {
  const [showFull, setShowFull] = useState(true)
  const details = useMemo(() => {
    try {
      return (data ? JSON.parse(data) : []) as Detail[]
    } catch {
      return [] as Detail[]
    }
  }, [data])
  const lengthSplit = details?.length > 2 ? 2 : details?.length

  return (
    <div className="">
      <div className="flex items-center justify-between">
        <p className="font-semibold text-sm text-primary-800 py-2">{UI_TEXT.PRODUCT_DESCRIPTION}</p>
        <div onClick={() => setShowFull((curr) => !curr)} className="cursor-pointer">
          {showFull ? (
            <ArrowUp2 size={12} color="#082F98" />
          ) : (
            <ArrowDown2 size={12} color="#082F98" />
          )}
        </div>
      </div>
      {details?.slice(0, !showFull ? lengthSplit : details?.length).map((detail: Detail, index) => (
        <div key={index} className="mb-0">
          {!!detail?.text?.trim() && (
            <p
              className={cn("font-normal break-words text-xss tiny:text-xs", {
                "mt-4": index > 0 && details?.[index - 1]?.type !== detail?.type
              })}
            >
              {detail?.text?.trim()}
            </p>
          )}
          {!!detail?.image?.url_list?.length && (
            <Image
              src={detail?.image?.url_list?.[0]}
              alt=""
              className="w-full m-auto h-auto object-contain"
              width={300}
              height={300}
            />
          )}
        </div>
      ))}
    </div>
  )
}

// Product Gallery
interface ProductGalleryProps {
  data?: Array<CampaignInspiration | DraftGallery>
  ownerId?: string
  prefix?: EFolderAssets
  withFullURL?: boolean
}

export function ProductGallery({
  data,
  ownerId,
  prefix = EFolderAssets.INSPIRATION_CAMPAIGN,
  withFullURL = false
}: ProductGalleryProps) {
  const images = useMemo(
    () =>
      data?.map((item) => ({
        original: withFullURL ? item.url : getImageByNameFromS3(item.name, ownerId, prefix),
        type: item.type
      })),
    [data, ownerId, prefix, withFullURL]
  )

  const [api, setApi] = useState<CarouselApi>()
  const [current, setCurrent] = useState(0)

  useEffect(() => {
    if (!api) {
      return
    }
    setCurrent(api.selectedScrollSnap() + 1)
    api.on("select", () => {
      setCurrent(api.selectedScrollSnap() + 1)
    })
  }, [api])
  if (!images?.length) return null

  return (
    <div className="relative w-full aspect-square overflow-hidden flex items-center justify-center bg-white rounded-md">
      <Carousel setApi={setApi} className="w-full h-full">
        <CarouselContent className="h-full">
          {images.map((item, index) => (
            <CarouselItem key={index} className="flex items-center justify-center">
              <Image
                className="object-cover object-center aspect-square w-full h-full bg-gray-50"
                width={1200}
                height={800}
                alt=""
                src={item.original}
              />
            </CarouselItem>
          ))}
        </CarouselContent>
        <div className="flex absolute bottom-2 left-0 right-0 justify-center gap-1">
          {images.map((image, index) => (
            <div
              key={index}
              className={cn(`w-1 h-1 rounded-full`, {
                "bg-black": current - 1 === index,
                "bg-white": current - 1 !== index
              })}
            />
          ))}
        </div>
      </Carousel>
    </div>
  )
}

// Product Reviews
interface AffiliateReviewItemProps {
  data: ReviewItem
}

export const AffiliateReviewItem: React.FC<AffiliateReviewItemProps> = ({ data }) => {
  return (
    <div className="flex flex-row gap-2 p-1.5 my-2">
      <div className="w-10 h-10 rounded-full bg-red-500 overflow-hidden">
        <Image
          className="w-full h-full rounded-full"
          src={data.reviewUser?.avatarUrl || ""}
          width={1000}
          height={1000}
          alt=""
        />
      </div>
      <div className="flex-1">
        <div className="flex flex-row justify-between">
          <p className="font-semibold text-xs">{data.reviewUser?.name || ""}</p>
          <div className="flex flex-row gap-1">
            <RatIcon color="yellow" />
          </div>
        </div>
        <div className="flex flex-row justify-between text-xs">
          <p className="font-normal text-xs text-gray-500">{data.skuSpecification || ""}</p>
        </div>
        <div className="flex flex-row justify-between">
          <p className="font-normal text-xs">{data?.review?.displayText}</p>
        </div>
      </div>
    </div>
  )
}
